// UserRegistrations.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '../../components/utils/Tooltip';

const CFOPanelUsers = () => {
  const authState = useSelector(state => state.authReducer);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [fetchData, { loading }] = useFetch();

  // Filters
  const [filters, setFilters] = useState({
    status: 'active', // active | inactive | all
    email: '',
    mobile: '',
    area: '',
    city: '',
    state: '',
    country: '',
    pin: '',
    fromDate: '',
    toDate: ''
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchUsers = useCallback(() => {
    const config = {
      url: "/userdata/userlist",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        filters
      }
    };

    fetchData(config, { showSuccessToast: false })
      .then(data => {
        if (Array.isArray(data)) {
          setUsers(data);
        }
      })
      .catch(err => console.error("Fetch error:", err));

  }, [authState.token, fetchData, filters]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchUsers();
  }, [authState.isLoggedIn, fetchUsers]);

  const toggleUser = (id) => {
    setSelectedUserId(prev => (prev === id ? null : id));
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [users]);

  const filterButtons = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'All', value: 'all' }
  ];

  return (
    <div className="p-4 text-white">

      {/* HEADER */}
      <h2 className="text-xl font-semibold border-b border-gray-600 pb-2">
        User Registrations
      </h2>

      {/* FILTER PANEL */}
      <div className="mt-4 bg-gray-900 p-4 rounded-lg shadow-lg">

        {/* Status buttons */}
        <div className="grid grid-cols-3 gap-1">
          {filterButtons.map(btn => (
            <button
              key={btn.value}
              onClick={() => handleFilterChange('status', btn.value)}
              className={`px-3 py-1 rounded text-sm transition ${
                filters.status === btn.value
                  ? 'bg-green-500 text-black'
                  : 'bg-gray-700'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* Inputs grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

          <input placeholder="Email"
            value={filters.email}
            onChange={e => handleFilterChange('email', e.target.value)}
            className="input"
          />

          <input placeholder="Mobile"
            value={filters.mobile}
            onChange={e => handleFilterChange('mobile', e.target.value)}
            className="input"
          />

          <input placeholder="Area"
            value={filters.area}
            onChange={e => handleFilterChange('area', e.target.value)}
            className="input"
          />

          <input placeholder="City"
            value={filters.city}
            onChange={e => handleFilterChange('city', e.target.value)}
            className="input"
          />

          <input placeholder="State"
            value={filters.state}
            onChange={e => handleFilterChange('state', e.target.value)}
            className="input"
          />

          <input placeholder="Country"
            value={filters.country}
            onChange={e => handleFilterChange('country', e.target.value)}
            className="input"
          />

          <input placeholder="PIN"
            value={filters.pin}
            onChange={e => handleFilterChange('pin', e.target.value)}
            className="input"
          />

          <input type="date"
            value={filters.fromDate}
            onChange={e => handleFilterChange('fromDate', e.target.value)}
            className="input"
          />

          <input type="date"
            value={filters.toDate}
            onChange={e => handleFilterChange('toDate', e.target.value)}
            className="input"
          />
        </div>

        <div className="mt-3 text-sm text-gray-300">
          Total Records: {users.length}
        </div>
      </div>

      {/* LOADER */}
      {loading && (
        <div className="flex justify-center mt-6">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}

      {/* LIST */}
      <div className="mt-4 space-y-2">

        {!loading && users.length === 0 && (
          <div className="text-center text-yellow-400 mt-10">
            No records found
          </div>
        )}

        {sortedUsers.map((user, index) => (
          <div key={user._id} className="bg-gray-800 text-gray-100 p-3 rounded shadow">

            <div className="flex items-center">
              <span>{index + 1}. {user.name}</span>

              <div className="ml-auto flex items-center gap-2">
                <Tooltip text="View">
                  <button onClick={() => toggleUser(user._id)}>
                    👁
                  </button>
                </Tooltip>
              </div>
            </div>

            {selectedUserId === user._id && (
              <div className="mt-2 bg-gray-700 [&_p]:text-gray-50 p-1 rounded text-xs lg:text-sm">
                <p><b>Email:</b> {user.email}</p>
                <p><b>Mobile:</b> {user.mobile}</p>
                <p><b>Status:</b> {user.isActive ? "Active" : "Inactive"}</p>
                <p><b>City:</b> {user.city}</p>
                <p><b>State:</b> {user.state}</p>
                <p><b>Country:</b> {user.country}</p>
                <p><b>PIN:</b> {user.pin}</p>
                <p><b>Registered:</b> {new Date(user.createdAt).toLocaleString()}</p>
              </div>
            )}

          </div>
        ))}
      </div>

      {/* INPUT STYLE (Tailwind helper suggestion) */}
      <style>{`
        .input {
          padding: 6px 10px;
          border-radius: 6px;
          background: #111827;
          border: 1px solid #374151;
          color: white;
          font-size: 13px;
        }
        .input:focus {
          outline: none;
          border-color: #10b981;
        }
      `}</style>

    </div>
  );
};

export default CFOPanelUsers;